import { useState, useEffect, useRef } from 'react'
import { Bell, ExternalLink } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import api from '../core/services/api'
import type { Notifica } from '../types/notifiche'

export default function NotificationBell() {
  const [count, setCount] = useState(0)
  const [notifiche, setNotifiche] = useState<Notifica[]>([])
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  // Polling conteggio ogni 60s
  useEffect(() => {
    loadCount()
    const interval = setInterval(loadCount, 60000)
    return () => clearInterval(interval)
  }, [])

  // Chiudi dropdown su click esterno
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const loadCount = async () => {
    try {
      const res = await api.get('/v1/notifiche/conteggio')
      setCount(res.data.conteggio || 0)
    } catch { /* silent */ }
  }

  const loadNotifiche = async () => {
    setLoading(true)
    try {
      const res = await api.get('/v1/notifiche?solo_non_lette=false&limit=15')
      setNotifiche(res.data)
    } catch { /* silent */ }
    setLoading(false)
  }

  const toggleDropdown = () => {
    if (!open) loadNotifiche()
    setOpen(!open)
  }

  const handleClick = async (n: Notifica) => {
    if (!n.letta) {
      try {
        await api.put(`/v1/notifiche/${n.id}/letta`)
        setCount(prev => Math.max(0, prev - 1))
        setNotifiche(prev => prev.map(x => x.id === n.id ? { ...x, letta: true } : x))
      } catch { /* silent */ }
    }
    if (n.link_url) {
      navigate(n.link_url)
      setOpen(false)
    }
  }

  const markAllRead = async () => {
    try {
      await api.put('/v1/notifiche/segna-tutte-lette')
      setCount(0)
      setNotifiche(prev => prev.map(x => ({ ...x, letta: true })))
    } catch { /* silent */ }
  }

  const formatDate = (iso: string) => {
    const d = new Date(iso)
    const now = new Date()
    const diffMs = now.getTime() - d.getTime()
    const diffMin = Math.floor(diffMs / 60000)
    if (diffMin < 60) return `${diffMin}m fa`
    const diffH = Math.floor(diffMin / 60)
    if (diffH < 24) return `${diffH}h fa`
    const diffD = Math.floor(diffH / 24)
    if (diffD < 7) return `${diffD}g fa`
    return d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={toggleDropdown}
        className="relative p-2 rounded-lg transition-colors hover:bg-white/10"
        title="Notifiche"
      >
        <Bell className="w-5 h-5" style={{ color: 'var(--color-text-secondary)' }} />
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-80 rounded-lg shadow-xl border z-50 max-h-96 overflow-y-auto"
          style={{
            backgroundColor: 'var(--color-bg-card)',
            borderColor: 'var(--color-border)',
          }}
        >
          <div className="flex items-center justify-between p-3 border-b" style={{ borderColor: 'var(--color-border)' }}>
            <span className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
              Notifiche
            </span>
            {count > 0 && (
              <button
                onClick={markAllRead}
                className="text-xs hover:underline"
                style={{ color: 'var(--color-accent, #3b82f6)' }}
              >
                Segna tutte lette
              </button>
            )}
          </div>

          {loading ? (
            <div className="p-4 text-center text-sm" style={{ color: 'var(--color-text-tertiary)' }}>
              Caricamento...
            </div>
          ) : notifiche.length === 0 ? (
            <div className="p-4 text-center text-sm" style={{ color: 'var(--color-text-tertiary)' }}>
              Nessuna notifica
            </div>
          ) : (
            notifiche.map(n => (
              <button
                key={n.id}
                onClick={() => handleClick(n)}
                className="w-full text-left p-3 border-b transition-colors hover:bg-white/5 flex items-start gap-2"
                style={{
                  borderColor: 'var(--color-border)',
                  opacity: n.letta ? 0.6 : 1,
                }}
              >
                {!n.letta && (
                  <span className="w-2 h-2 rounded-full bg-blue-500 mt-1.5 flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate" style={{ color: 'var(--color-text-primary)' }}>
                    {n.titolo}
                  </p>
                  {n.messaggio && (
                    <p className="text-xs mt-0.5 line-clamp-2" style={{ color: 'var(--color-text-secondary)' }}>
                      {n.messaggio}
                    </p>
                  )}
                  <p className="text-[10px] mt-1" style={{ color: 'var(--color-text-tertiary)' }}>
                    {formatDate(n.created_at)}
                  </p>
                </div>
                {n.link_url && (
                  <ExternalLink className="w-3.5 h-3.5 mt-1 flex-shrink-0" style={{ color: 'var(--color-text-tertiary)' }} />
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}
